import { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { IItemKey } from 'api/catalog/models';
import { getTypedKeys } from 'utils/getTypedKeys';

import messages from './messages';

export interface ItemAttributeInfo {
  id: IItemKey;
  friendlyName?: string;
  description?: string;
}

export const useItemAttributes = () => {
  const { formatMessage } = useIntl();
  const attributeMap = useMemo((): Record<IItemKey, ItemAttributeInfo> => {
    return {
      assets: {
        id: 'assets',
        friendlyName: formatMessage(messages.assets),
      },
      itemNumber: {
        id: 'itemNumber',
        friendlyName: formatMessage(messages.itemNumber),
      },
      isSupplierShipped: {
        id: 'isSupplierShipped',
        friendlyName: formatMessage(messages.isSupplierShipped),
      },
      shopId: {
        id: 'shopId',
        friendlyName: formatMessage(messages.shopId),
      },
      offerId: {
        id: 'offerId',
        friendlyName: formatMessage(messages.offerId),
      },
      shopName: {
        id: 'shopName',
        friendlyName: formatMessage(messages.shopName),
      },
      shopDescription: {
        id: 'shopDescription',
        friendlyName: formatMessage(messages.shopDescription),
      },
      shopReturnPolicy: {
        id: 'shopReturnPolicy',
        friendlyName: formatMessage(messages.shopReturnPolicy),
      },
      minShopOrderValue: {
        id: 'minShopOrderValue',
        friendlyName: formatMessage(messages.minShopOrderValue),
      },
      minOrderQty: {
        id: 'minOrderQty',
        friendlyName: formatMessage(messages.minOrderQty),
      },
      maxOrderQty: {
        id: 'maxOrderQty',
        friendlyName: formatMessage(messages.maxOrderQty),
      },
      grossWeight: {
        id: 'grossWeight',
        friendlyName: formatMessage(messages.grossWeight),
      },
      title: {
        id: 'title',
        friendlyName: formatMessage(messages.title),
      },
      id: {
        id: 'id',
        friendlyName: formatMessage(messages.id),
      },
      itemId: {
        id: 'itemId',
        friendlyName: formatMessage(messages.itemId),
      },
      hostSystem: {
        id: 'hostSystem',
        friendlyName: formatMessage(messages.hostSystem),
      },
      upc: {
        id: 'upc',
        friendlyName: formatMessage(messages.upc),
      },
      plu: {
        id: 'plu',
        friendlyName: formatMessage(messages.plu),
      },
      brandId: {
        id: 'brandId',
        friendlyName: formatMessage(messages.brandId),
      },
      brandName: {
        id: 'brandName',
        friendlyName: formatMessage(messages.brandName),
      },
      description: {
        id: 'description',
        friendlyName: formatMessage(messages.description),
      },
      extendedDescription: {
        id: 'extendedDescription',
        friendlyName: formatMessage(messages.extendedDescription),
      },
      countryOfOriginCode: {
        id: 'countryOfOriginCode',
        friendlyName: formatMessage(messages.countryOfOriginCode),
      },
      countryOfOriginName: {
        id: 'countryOfOriginName',
        friendlyName: formatMessage(messages.countryOfOriginName),
      },
      ingredients: {
        id: 'ingredients',
        friendlyName: formatMessage(messages.ingredients),
      },
      organicCode: {
        id: 'organicCode',
        friendlyName: formatMessage(messages.organicCode),
      },
      departmentId: {
        id: 'departmentId',
        friendlyName: formatMessage(messages.departmentId),
      },
      departmentName: {
        id: 'departmentName',
        friendlyName: formatMessage(messages.departmentName),
      },
      categoryId: {
        id: 'categoryId',
        friendlyName: formatMessage(messages.categoryId),
      },
      categoryName: {
        id: 'categoryName',
        friendlyName: formatMessage(messages.categoryName),
      },
      subcategoryId: {
        id: 'subcategoryId',
        friendlyName: formatMessage(messages.subcategoryId),
      },
      subcategoryName: {
        id: 'subcategoryName',
        friendlyName: formatMessage(messages.subcategoryName),
      },
      statusCode: {
        id: 'statusCode',
        friendlyName: formatMessage(messages.statusCode),
      },
      itemAttributes: {
        id: 'itemAttributes',
        friendlyName: formatMessage(messages.itemAttributes),
      },
      // availableAtDcNumbers: {
      //   id: 'availableAtDcNumbers',
      //   friendlyName: formatMessage(messages.availableAtDcNumbers),
      // },
      dcNumber: {
        id: 'dcNumber',
        friendlyName: formatMessage(messages.dcNumber),
      },
      dcName: {
        id: 'dcName',
        friendlyName: formatMessage(messages.dcName),
      },
      promoAtDcNumbers: {
        id: 'promoAtDcNumbers',
        friendlyName: formatMessage(messages.promoAtDcNumbers),
      },
      organicCodePercentage: {
        id: 'organicCodePercentage',
        friendlyName: formatMessage(messages.organicCodePercentage),
      },
      packConfigCode: {
        id: 'packConfigCode',
        friendlyName: formatMessage(messages.packConfigCode),
      },
      packQty: {
        id: 'packQty',
        friendlyName: formatMessage(messages.packQty),
      },
      packSize: {
        id: 'packSize',
        friendlyName: formatMessage(messages.packSize),
      },
      corpItemCode: {
        id: 'corpItemCode',
        friendlyName: formatMessage(messages.corpItemCode),
      },
      isVariableWeight: {
        id: 'isVariableWeight',
        friendlyName: formatMessage(messages.isVariableWeight),
      },
      isPrivateLabel: {
        id: 'isPrivateLabel',
        friendlyName: formatMessage(messages.isPrivateLabel),
      },
      sugarTaxZipcodes: {
        id: 'sugarTaxZipcodes',
        friendlyName: formatMessage(messages.sugarTaxZipcodes),
      },
      bottleDepositStates: {
        id: 'bottleDepositStates',
        friendlyName: formatMessage(messages.bottleDepositStates),
      },
      isLongLeadItem: {
        id: 'isLongLeadItem',
        friendlyName: formatMessage(messages.isLongLeadItem),
      },
      leadTimeDays: {
        id: 'leadTimeDays',
        friendlyName: formatMessage(messages.leadTimeDays),
      },
      elasticId: {
        id: 'elasticId',
        friendlyName: formatMessage(messages.elasticId),
      },
      itemAtOtherDcs: {
        id: 'itemAtOtherDcs',
        friendlyName: formatMessage(messages.itemAtOtherDcs),
      },
      additionalDetails: {
        id: 'additionalDetails',
        friendlyName: formatMessage(messages.additionalDetails),
      },
    };
  }, [formatMessage]);

  const isItemAttribute = useCallback(
    (str: string) => Object.keys(attributeMap).includes(str),
    [attributeMap]
  );

  const getLabel = useCallback(
    (key: IItemKey) => {
      const foundAttrInfo = attributeMap[key];
      return foundAttrInfo ? foundAttrInfo.friendlyName : key;
    },
    [attributeMap]
  );

  const attributes = useMemo(() => getTypedKeys(attributeMap), [attributeMap]);

  return {
    attributeMap,
    attributes,
    isItemAttribute,
    getLabel,
  };
};
